// Shared SEO helpers for tour detail routes so that /tour/$slug and
// /$lang/tour/$slug emit identical Product metadata plus hreflang alternates.
import { cleanTourTitle, metaDescription } from "./presentation-text";
import type { PublicReview } from "./verified-reviews.functions";
import type { AppLang } from "@/i18n";
import { SUPPORTED_LANGS } from "@/i18n";
import { OG_CORPORATE_IMAGE, ogPhotoCoverUrl } from "@/lib/og";
import { tourCardImageUrl, tourCardVersion } from "@/lib/tour-card";

export const SITE_URL = "https://www.mostrandomexico.mx";

// Default language is served at /tour/$slug (no prefix) and marked x-default.
export const DEFAULT_TOUR_LANG: AppLang = "es";

export const TOUR_LOCALE_TAGS: Record<AppLang, string> = {
  es: "es-MX",
  en: "en-US",
  fr: "fr-FR",
  pt: "pt-BR",
  it: "it-IT",
  de: "de-DE",
};

export function tourUrlForLang(lang: AppLang, slug: string): string {
  if (lang === DEFAULT_TOUR_LANG) return `${SITE_URL}/tour/${slug}`;
  return `${SITE_URL}/${lang}/tour/${slug}`;
}

export function toAbsoluteUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("//")) return `https:${url}`;
  return `${SITE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

export interface TourHeadInput {
  loaderData?: {
    title: string;
    description: string;
    image?: string;
    cardImage?: string;
    place?: string | null;
    priceFrom?: number | null;
    currency?: string;
    ratingAvg?: number;
    ratingCount?: number;
    reviews?: PublicReview[];
  };
  slug: string;
  lang: AppLang;
}

export function buildTourHead({ loaderData, slug, lang }: TourHeadInput) {
  if (!loaderData) {
    return {
      meta: [{ title: "Tour — Mostrando México" }, { name: "robots", content: "noindex,nofollow" }],
    };
  }

  loaderData = { ...loaderData, title: cleanTourTitle(loaderData.title), description: metaDescription(loaderData.description) };
  const url = tourUrlForLang(lang, slug);
  const title = `${loaderData.title} — Mostrando México`;
  const currency = loaderData.currency || "MXN";

  // Social card: the real product photo with gradient, tour name, destination
  // and corporate logo composed on top; corporate image when there is no photo.
  // Tarjeta horizontal 1200×630 con la plantilla común; URL versionada por
  // título, precio, moneda y foto para que las redes vuelvan a leerla.
  const socialImage = loaderData.image
    ? ogPhotoCoverUrl(loaderData.image)
    : OG_CORPORATE_IMAGE;

  const meta: Array<Record<string, string>> = [
    { title },
    { name: "description", content: loaderData.description },
    { property: "og:title", content: loaderData.title },
    { property: "og:description", content: loaderData.description },
    { property: "og:type", content: "product" },
    { property: "og:url", content: url },
    { property: "og:site_name", content: "Mostrando México" },
    { property: "og:locale", content: TOUR_LOCALE_TAGS[lang] },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: loaderData.title },
    { name: "twitter:description", content: loaderData.description },
  ];

  // og:locale:alternate for the remaining supported languages.
  for (const l of SUPPORTED_LANGS) {
    if (l === lang) continue;
    meta.push({ property: "og:locale:alternate", content: TOUR_LOCALE_TAGS[l] });
  }

  meta.push(
    { property: "og:image", content: socialImage },
    { property: "og:image:secure_url", content: socialImage },
    { property: "og:image:type", content: "image/jpeg" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: loaderData.title },
    { name: "twitter:image", content: socialImage },
    { name: "twitter:image:alt", content: loaderData.title },
  );

  const productLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: loaderData.title,
    description: loaderData.description,
    ...(loaderData.image ? { image: [loaderData.image] } : {}),
    url,
    inLanguage: TOUR_LOCALE_TAGS[lang],
    brand: { "@type": "Brand", name: "Mostrando México", alternateName: "MostrandoMexico" },
    seller: { "@type": "Organization", name: "Mostrando México", alternateName: "MostrandoMexico", url: SITE_URL },

  };
  if (typeof loaderData.priceFrom === "number" && Number.isFinite(loaderData.priceFrom) && loaderData.priceFrom > 0) {
    productLd.offers = { "@type": "Offer", url, priceCurrency: currency, price: loaderData.priceFrom.toFixed(2) };
  }
  if (loaderData.reviews?.length && (loaderData.ratingCount ?? 0) > 0 && (loaderData.ratingAvg ?? 0) > 0) {
    productLd.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: String(loaderData.ratingAvg),
      reviewCount: String(loaderData.ratingCount),
      bestRating: "5",
      worstRating: "1",
    };
  }

  if (loaderData.reviews?.length) productLd.review = loaderData.reviews.slice(0,10).map(r => ({
    "@type": "Review", name: r.title, reviewBody: r.body, inLanguage: r.language,
    author: { "@type": "Person", name: r.traveler_name }, datePublished: r.created_at,
    reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 },
  }));
  // Canonical is self-referential per language, plus hreflang for every
  // supported language and an x-default pointing at the Spanish version.
  const links: Array<Record<string, string>> = [
    { rel: "canonical", href: url },
    { rel: "alternate", hrefLang: "x-default", href: tourUrlForLang(DEFAULT_TOUR_LANG, slug) },
  ];
  for (const l of SUPPORTED_LANGS) {
    links.push({
      rel: "alternate",
      hrefLang: TOUR_LOCALE_TAGS[l],
      href: tourUrlForLang(l, slug),
    });
  }

  return {
    meta,
    links,
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(productLd).replace(/</g, "\\u003c"),
      },
    ],
  };
}
